import {
  lazySelector,
  restrictScroll,
  unleashScroll,
  getCurrentlyFocusableElements,
  saveActiveElement,
} from '@phx-styleguides/core';

import { assertIsHTMLElement, Transition } from '@sg-core/resources/assets/js/utils';

export interface ModalOptions {
  onShow?: (event?: Event) => void;
  onShown?: (event?: Event) => void;
  onHide?: (event?: Event) => void;
  onHidden?: (event?: Event) => void;
  triggerOpenAttribute?: string;
  triggerCloseAttribute?: string;
  triggerToggleAttribute?: string;
  overlayClass?: string;
  containerClass?: string;
  closable?: boolean;
  restoreActiveElement?: boolean;
  closeAll?: boolean;
  updateTabsIndicator?: boolean;
  focusOnOpen?: string;
}

const STATES = {
  active: 'is-active',
} as const;

const openModals: Modal[] = [];

export class Modal {
  private el: HTMLElement;
  private opts: ModalOptions;
  private active: boolean;
  private overlayEl: HTMLElement | null;
  private containerEl: HTMLElement | null;
  private focusableElements: HTMLElement[] = [];
  private restoreActiveElement: (() => void) | null = null;
  private transitionInstance: Transition | null = null;
  private elemToFocusOnOpen: string | null;
  private abortController: AbortController;

  constructor(element: string | HTMLElement, options: ModalOptions = {}) {
    this.el = lazySelector(element) as HTMLElement;
    this.abortController = new AbortController();

    this.opts = {
      triggerOpenAttribute: 'data-modal-open',
      triggerCloseAttribute: 'data-modal-close',
      triggerToggleAttribute: 'data-modal-toggle',
      overlayClass: 'modal__overlay',
      containerClass: 'modal__container',
      closable: true,
      restoreActiveElement: true,
      closeAll: false,
      updateTabsIndicator: false,
      ...options,
    };

    this.active = this.el.classList.contains(STATES.active);
    this.elemToFocusOnOpen = this.opts.focusOnOpen ?? null;

    this.overlayEl = this.el.querySelector(`.${this.opts.overlayClass}`);
    this.containerEl = this.el.querySelector(`.${this.opts.containerClass}`);

    document.addEventListener('click', this.onClick.bind(this), { signal: this.abortController.signal });
  }

  private onClick(event: MouseEvent) {
    const target = event.target as Element;
    const el = this.rootElement;
    const openAttr = this.options.triggerOpenAttribute;
    const closeAttr = this.options.triggerCloseAttribute;

    const isOpenTrigger = !!target.closest(`button[${openAttr}="${el.id}"]`);
    const isCloseTrigger = !!target.closest(`button[${closeAttr}="${el.id}"]`);
    const isToggleTrigger = !!target.closest(`button[${this.opts.triggerToggleAttribute}="${el.id}"]`);

    if (isOpenTrigger && !this.isActive) {
      event.stopImmediatePropagation();
      return this.dispatch('show', event);
    } else if (
      (isCloseTrigger && this.isActive) ||
      (isToggleTrigger && this.isActive) ||
      event.target === this.overlayElement
    ) {
      event.stopImmediatePropagation();
      return this.dispatch('hide', event);
    }
  }

  private onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Esc':
      case 'Escape':
        if (this === openModals[openModals.length - 1]) {
          this.dispatch('hide', event);
        }
        break;
      case 'Tab':
        this.refreshFocusableElements();

        if (this.focusableElements.length === 1) {
          event.preventDefault();
        } else if (event.shiftKey) {
          if (this.lastFocusableEl && document.activeElement === this.firstFocusableEl) {
            event.preventDefault();
            this.lastFocusableEl.focus();
          }
        } else {
          if (this.firstFocusableEl && document.activeElement === this.lastFocusableEl) {
            event.preventDefault();
            this.firstFocusableEl.focus();
          }
        }
        break;
    }
  }

  protected dispatch(action: string, event: Event) {
    switch (action) {
      case 'show':
        return this.show(event);
      case 'hide':
        return this.hide(event);
      default:
        throw new Error(`Unknown action: ${action}`);
    }
  }

  private refreshFocusableElements() {
    if (!this.containerEl) {
      return;
    }

    this.focusableElements = getCurrentlyFocusableElements(this.containerEl) as HTMLElement[];
  }

  private get firstFocusableEl() {
    return this.focusableElements[0];
  }

  private get lastFocusableEl() {
    return this.focusableElements[this.focusableElements.length - 1];
  }

  private get transitioningElement() {
    return this.overlayEl || this.containerEl;
  }

  private customFocus() {
    if (!this.elemToFocusOnOpen) {
      return;
    }

    const el = this.el.querySelector<HTMLElement>(`#${this.elemToFocusOnOpen}`);

    el?.focus();
  }

  protected get rootElement() {
    return this.el;
  }

  protected get overlayElement() {
    return this.overlayEl;
  }

  protected get options() {
    return this.opts;
  }

  protected get isActive() {
    return this.active;
  }

  public show(event?: Event) {
    if (this.opts.closeAll) {
      Modal.closeAll();
    }

    if (this.active) {
      return Promise.resolve();
    }

    openModals.push(this);

    this.active = true;
    this.transitionInstance?.destroy();

    this.restoreActiveElement = saveActiveElement();
    document.addEventListener('keydown', this.onKeydown.bind(this));

    this.opts.onShow?.(event);

    restrictScroll();

    return new Promise<void>((resolve) => {
      assertIsHTMLElement(this.transitioningElement);

      this.transitionInstance = new Transition({
        element: this.transitioningElement,
        event: 'animationend',
        before: () => {
          this.el.classList.add(STATES.active);
        },
        transition: () => {
          this.el.setAttribute('aria-hidden', 'false');

          if (this.opts.updateTabsIndicator) {
            window.dispatchEvent(new Event('updateTabsIndicator'));
          }
        },
        after: () => {
          if (this.elemToFocusOnOpen) {
            this.customFocus();
          } else {
            this.refreshFocusableElements();
            this.firstFocusableEl?.focus({ preventScroll: true });
          }

          this.opts.onShown?.(event);
          this.transitionInstance = null;

          resolve();
        },
      });
    });
  }

  public hide(event?: Event) {
    if (this.active && !this.opts.closable && event?.isTrusted) {
      return Promise.resolve();
    }

    const idx = openModals.indexOf(this);

    if (idx >= 0) {
      openModals.splice(idx, 1);
    }

    this.transitionInstance?.destroy();
    document.removeEventListener('keydown', this.onKeydown.bind(this));

    this.opts.onHide?.(event);

    this.restoreActiveElement?.();

    if (openModals.length === 0) {
      unleashScroll();
    }

    return new Promise<void>((resolve) => {
      assertIsHTMLElement(this.transitioningElement);

      this.transitionInstance = new Transition({
        element: this.transitioningElement,
        event: 'animationend',
        transition: () => {
          this.el.setAttribute('aria-hidden', 'true');
        },
        after: () => {
          this.el.classList.remove(STATES.active);
          this.opts.onHidden?.(event);
          this.transitionInstance = null;
          this.active = false;

          resolve();
        },
      });
    });
  }

  public setClosable(value: boolean) {
    this.opts.closable = value;
  }

  public destroy() {
    document.removeEventListener('click', this.onClick.bind(this));
    document.removeEventListener('keydown', this.onKeydown.bind(this));
  }

  public static closeAll() {
    while (openModals.length) {
      openModals[openModals.length - 1].hide();
    }
  }
}
